import { useCallback, useMemo } from 'react';
import { ASSETS_PREVIEW, ASSETS_TYPE, FILTER_PARAMS, PORTAL_TARGETS } from '@/types/shared/navigation-tabs.enums';
import { useSearchParams } from 'react-router-dom';
import { BusinessInsights } from '@/components/pages/AssetsPreviewPage';
import { AssetsOverview } from '@/components/pages/AssetsPreviewPage/AssetsOverview';
import { useAppFilters } from '@/hooks/shared';
import { Layout } from '@/layouts';
import { BaseTabs } from '@/modules';
import { AssetPreviewFilter } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetsPreviewFilter/AssetPreviewFilter';
import { FaultsFilter } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewTabsViews/Faults/components/FaultsFilter';
import { AssetPreviewViewsSelectors } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewViewsSelectors/AssetPreviewViewsSelectors';
import { dispatch, RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { AssetsMaintenance } from '@/components/pages/AssetsPreviewPage/Maintenance';
import { DatePicker } from '@/components/uiKit/DatePicker/DatePicker';
import { useFetchAssets } from '@/hooks';
import { cn } from '@/lib/utils';
import { setDateRangePickerDate } from '@/store/slices/assetsPreviewSlice';
import { OperatorProficiencyPage } from '@/modules/AssetsPreviewPage/OperatorProficiency';
import { TimeRangeSelector } from '@/modules/AssetsPreviewPage/OperatorProficiency/components/TimeRangeSelector';
import { AssetTypeDropdown } from '@/modules/AssetsPreviewPage/OperatorProficiency/components/AssetTypeDropdown';
import { OperatorProficiencyCSVExport } from '@/modules/AssetsPreviewPage/OperatorProficiency/components/OperatorProficiencyCSVExport';
import { OperatorProficiencyProvider } from '@/contexts/OperatorProficiencyContext';

export const AssetsPreviewPage = () => {
  const { assetsType, assetsPreview } = useAppFilters();
  const { selectedView } = useSelector((state: RootState) => state.assetsPreview);

  const { assets } = useFetchAssets();

  const onDateUpdate = (range: any) => {
    // Convert Date objects to ISO strings to avoid Redux serialization warnings
    const serializedRange = {
      from: range.from instanceof Date ? range.from.toISOString() : range.from,
      to: range.to instanceof Date ? range.to.toISOString() : range.to,
    };
    dispatch(setDateRangePickerDate(serializedRange));
  };

  const sitesPageTabs = useMemo(() => {
    const tabs = [
      { id: ASSETS_PREVIEW.ASSETS, title: 'Assets', element: <AssetsOverview /> },
      {
        id: ASSETS_PREVIEW.BI,
        title: 'Business Insights',
        element: <BusinessInsights />,
        disabled: assetsType !== ASSETS_TYPE.FIXED,
      },
      // {
      //   id: ASSETS_PREVIEW.MAP,
      //   title: 'Site Map',
      //   // disabled: assetsType !== ASSETS_TYPE.MOBILE,
      //   disabled: true,
      //   element: (
      //     <div className="absolute top-0 left-0 h-full w-full z-0">
      //       <SiteMap />
      //     </div>
      //   ),
      // },
      {
        id: ASSETS_PREVIEW.MAINTENANCE_DATA,
        title: 'Maintenance Data',
        element: <AssetsMaintenance assets={assets} />,
      },
    ];

    // Only show Operator Proficiency tab for mobile assets
    if (assetsType === ASSETS_TYPE.MOBILE) {
      tabs.push({
        id: ASSETS_PREVIEW.OPERATOR_PROFICIENCY,
        title: 'Operator Proficiency',
        element: <OperatorProficiencyPage />,
      });
    }

    return tabs;
  }, [assetsType, assets]);

  const renderRightPane = useCallback(() => {
    const { limitUnit, limitValue }: { limitUnit: 'days' | 'months'; limitValue: number } = useMemo(() => {
      return {
        limitUnit: assetsPreview === ASSETS_PREVIEW.ALARMS ? 'days' : 'months',
        limitValue: assetsPreview === ASSETS_PREVIEW.ALARMS ? 7 : 2,
      };
    }, [assetsPreview]);
    return (
      <>
        {assetsPreview === ASSETS_PREVIEW.OPERATOR_PROFICIENCY && assetsType === ASSETS_TYPE.MOBILE ? (
          <div className="flex items-center justify-between w-full pl-4 pr-4 ml-4 border-l border-gray-300">
            <AssetTypeDropdown />
            <div className="flex items-center gap-4">
              <TimeRangeSelector />
              <OperatorProficiencyCSVExport />
            </div>
          </div>
        ) : (
          !!assets && (
            <div
              className={cn(
                'flex items-center justify-between pl-6 gap-6',
                assetsPreview === ASSETS_PREVIEW.ASSETS ? 'xl:ml-6 xl:border-l xl:border-[#B1B9C3]' : ''
              )}
            >
              <div className="flex gap-6 w-full justify-between">
                {/* AlarmsFilter removed - was marked DELETE IF NOT NEEDED */}
              </div>
              {(assetsPreview === ASSETS_PREVIEW.MAINTENANCE_DATA || assetsPreview === ASSETS_PREVIEW.ALARMS) && (
                <div className="justify-end flex items-center gap-4">
                  <DatePicker limitUnit={limitUnit} limitValue={limitValue} onDateUpdate={onDateUpdate} />
                </div>
              )}
            </div>
          )
        )}
      </>
    );
  }, [assetsPreview, assetsType, selectedView, assets]);

  const renderCustomPane = useCallback(() => {
    const [searchParams] = useSearchParams();
    const assetsSection = searchParams.get(FILTER_PARAMS.ASSETS_SECTION);

    // Explicit checks for each section type for better code clarity
    const isFaultsSection = assetsSection === 'faults';
    const isOverviewSection = assetsSection === 'overview' || !assetsSection; // Default to overview if no section specified
    const shouldShowSelectors = assetsPreview === ASSETS_PREVIEW.ASSETS && isOverviewSection;

    return (
      <div className="flex items-center">
        <div className="w-[320px] h-full" id={PORTAL_TARGETS.ASSETS_SEARCH_CONTAINER}></div>
        <div className="flex-1">
          {assetsPreview === ASSETS_PREVIEW.ASSETS && (
            <div className={cn('flex items-center justify-between pl-6 gap-6')}>
              <div className="flex gap-6 w-full justify-between">
                {isFaultsSection && <FaultsFilter />}
                {isOverviewSection && !!assets && <AssetPreviewFilter assets={assets} />}
                {shouldShowSelectors && <AssetPreviewViewsSelectors selectedView={selectedView} />}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [assetsPreview, selectedView, assets]);
  const pageContent = (
    <Layout className={assetsPreview === ASSETS_PREVIEW.ASSETS ? 'max-h-main-mobile-assets' : ''}>
      <div className="flex flex-col md:px-8 w-full h-full gap-4 px-2 py-0 max-h-screen ">
        <BaseTabs
          classNames={{
            tabContent: assetsPreview !== ASSETS_PREVIEW.ASSETS ? 'overflow-auto' : 'max-h-main-mobile-assets h-full',
          }}
          tabs={sitesPageTabs}
          defaultValue={assetsPreview || sitesPageTabs[0].id}
          paramAlias={FILTER_PARAMS.ASSETS_PREVIEW}
          hideTabTriggers={false}
          renderRightPane={renderRightPane}
          renderCustomPane={renderCustomPane}
          renderFixedElement={undefined}
          createContainerById={PORTAL_TARGETS.ASSETS_PREVIEW_TABS_CONTAINER}
        />
      </div>
    </Layout>
  );

  // Wrap in provider only when on operator proficiency tab AND on mobile fleet
  if (assetsPreview === ASSETS_PREVIEW.OPERATOR_PROFICIENCY && assetsType === ASSETS_TYPE.MOBILE) {
    return <OperatorProficiencyProvider>{pageContent}</OperatorProficiencyProvider>;
  }

  return pageContent;
};
